import { OrderDirection } from "_shared/_api/ordering/order-direction";
import EntityMember from "_shared/entity-members/entity-member";
import { useEffect, useState } from "react";
import { useSession } from "users/__session/session-context";
import UserCoworkerQuery, {
  UserCoworkerFilterParameters,
  UserCoworkerOrderField,
  UserCoworkerOrderParameters,
} from "users/information/coworker-information/_api/commands/query-user-coworker-api-request";
import { useLazyGetUserCoworkersQuery } from "users/information/coworker-information/_api/user-coworker-api";
import {
  ColleaguePageFetch,
  colleaguesToAdd,
  fetchAllColleagues,
} from "work/work-agreements/teams/vendor-team/_views/members/add-all-colleagues";

/**
 * The firm's roster, with the author first.
 *
 * At module scope deliberately: it holds a try/catch, which the React Compiler cannot lower, and a
 * bailout inside the hook would cost the whole hook its memoization. See app/CLAUDE.md.
 *
 * The author alone is the answer when the roster cannot be loaded, and it is a truthful one — they
 * can still name themselves, and an empty list would make the editor say the field cannot be
 * filled at all.
 */
export async function loadFeeScheduleTemplateTeamMembers(
  author: EntityMember,
  fetchPage: ColleaguePageFetch,
): Promise<EntityMember[]> {
  try {
    const roster = await fetchAllColleagues(fetchPage);
    // `colleaguesToAdd` is what the vendor-team roster converts the same payload with. Handing it
    // the author as already seen is what keeps them out of the list twice.
    return [author, ...colleaguesToAdd(roster.colleagues, [author.userId])];
  } catch (error) {
    console.error("Error loading colleagues for the fee schedule template", error);
    return [author];
  }
}

/**
 * Who a fee schedule template's hourly rate can be for: the author and their colleagues.
 *
 * A proposal's fee schedule reads this list off the proposal's vendor team
 * (`getFeeScheduleTeamMembers`), which is the right answer there and no answer at all here — a
 * template belongs to a firm and to no engagement, so it has no team. The firm's own roster is the
 * only roster it has, and the author is on it: a partner writing their own rate should be able to
 * name themselves, and `users/coworkers` answers with everybody else.
 *
 * Nobody is checked against a proposal here, because a template cannot know which proposal it will
 * be applied to. `WorkAgreement.ValidateFeeSchedule` is where an assignment finally has to hold,
 * against that proposal's vendor team, at hire time — and the apply path asks the user what to do
 * about anyone who is not on it.
 */
export default function useFeeScheduleTemplateTeamMembers(): readonly EntityMember[] {
  const session = useSession();
  const [fetchColleaguePage] = useLazyGetUserCoworkersQuery();
  const [teamMembers, setTeamMembers] = useState<readonly EntityMember[]>([]);
  const authorUserId = session.user?.id;
  const authorLegalEntityId = session.user?.legalEntityId;

  useEffect(() => {
    if (!authorUserId || !authorLegalEntityId) return;
    let cancelled = false;
    const author = new EntityMember({ userId: authorUserId, legalEntityId: authorLegalEntityId });
    const fetchPage: ColleaguePageFetch = (pagination) =>
      fetchColleaguePage({
        query: new UserCoworkerQuery(
          new UserCoworkerFilterParameters(),
          new UserCoworkerOrderParameters(UserCoworkerOrderField.Name, OrderDirection.Ascending),
          pagination,
        ),
        append: false,
      }).unwrap();

    void loadFeeScheduleTemplateTeamMembers(author, fetchPage).then((members) => {
      if (!cancelled) setTeamMembers(members);
    });

    return () => {
      cancelled = true;
    };
  }, [authorLegalEntityId, authorUserId, fetchColleaguePage]);

  return teamMembers;
}
